import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'

// Frontend shape for /session-quota/me.
export interface SessionQuota {
  tier_code: 'free' | 'estudante' | 'premium'
  tier_name: string
  mini: {
    used_this_week: number
    limit_per_week: number | null
    next_available_at: string | null
  }
  full: {
    lifetime_completed: number
    lifetime_limit: number | null
    locked_forever: boolean
    month_completed: number
    month_limit: number | null
    month_resets_at: string
  }
  practice: {
    questions_used_today: number
    questions_limit_today: number | null
  }
}

interface UseSessionQuotaResult {
  quota: SessionQuota | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

// Fetches /session-quota/me on mount and exposes a manual refresh.
export function useSessionQuota(): UseSessionQuotaResult {
  const [quota, setQuota] = useState<SessionQuota | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<SessionQuota>('/session-quota/me')
      setQuota(res)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar quota')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return { quota, loading, error, refresh: load }
}

export function canStartMini(quota: SessionQuota | null): boolean {
  if (!quota) return false
  const limit = quota.mini.limit_per_week
  if (limit === null) return true
  return quota.mini.used_this_week < limit
}

export function canStartFull(quota: SessionQuota | null): boolean {
  if (!quota) return false
  if (quota.full.locked_forever) return false
  const lifetime = quota.full.lifetime_limit
  if (lifetime !== null && quota.full.lifetime_completed >= lifetime) return false
  const monthly = quota.full.month_limit
  if (monthly !== null && quota.full.month_completed >= monthly) return false
  return true
}
